import { useState, useCallback } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import {
	APP_TEAM_NAME,
	useExperienceTracker,
} from '@atlassian/jira-project-create-importer-common';
import { APP_IDENTIFIER } from '../../common/constants';
import {
	EXPERIENCES,
	thirdPartyMultiEntityStatsInteractionMetric,
} from '../../common/constants/metrics';
import { useThirdPartyClient } from '../../common/utils/third-party-clients';
import type { UseThirdPartyMultiEntityStats, UseThirdPartyMultiEntityStatsProps } from './types';

export const useThirdPartyMultiEntityStats = ({
	thirdParty,
	requestAuthenticationToken,
}: UseThirdPartyMultiEntityStatsProps): UseThirdPartyMultiEntityStats => {
	const thirdPartyClient = useThirdPartyClient({
		thirdParty,
		requestAuthenticationToken,
	});
	const [onExperienceStart, onExperienceSuccess, onExperienceFail] = useExperienceTracker(
		EXPERIENCES.THIRD_PARTY_MULTI_ENTITY_STATS,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [hasErrorOccurred, setHasErrorOccurred] = useState(false);

	const fetchThirdPartyMultiEntityStats = useCallback(
		async (entityIds: string[]) => {
			setIsLoading(true);
			setHasErrorOccurred(false);
			thirdPartyMultiEntityStatsInteractionMetric.start();
			onExperienceStart();

			try {
				const data = await thirdPartyClient.getMultiEntityStats({ entityIds });
				thirdPartyMultiEntityStatsInteractionMetric.stop();
				onExperienceSuccess();

				setIsLoading(false);
				return data;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				thirdPartyMultiEntityStatsInteractionMetric.stop();
				onExperienceFail(APP_IDENTIFIER, error, { thirdParty });
				fireErrorAnalytics({
					meta: {
						id: 'fetch-third-party-multi-entity-stats',
						packageName: APP_IDENTIFIER,
						teamName: APP_TEAM_NAME,
					},
					sendToPrivacyUnsafeSplunk: true,
					error,
				});

				setHasErrorOccurred(true);
				setIsLoading(false);
				return null;
			}
		},
		[onExperienceFail, onExperienceStart, onExperienceSuccess, thirdPartyClient, thirdParty],
	);

	return { isLoading, hasErrorOccurred, fetchThirdPartyMultiEntityStats };
};
